import { useAppToast } from "@/hooks/useAppToast"
import { formatFileSize, getFileExtension } from "@/utils/files/file"
import { isNotBlank } from "@/utils/ts-utils"
import { Flex, Input, type InputProps, forwardRef } from "@chakra-ui/react"
import { type ChangeEvent, type ForwardedRef, useMemo, useState } from "react"
import type { FieldValues, UseFormReturn } from "react-hook-form"
import { FormattedMessage } from "react-intl"
import { FileExportIcon } from "../Icons"
import { P3 } from "../Typographies"
import { useValidateFiles } from "./utils/validateFiles"

type Props = InputProps & {
  modalForm: unknown
  buttonLabel?: string
  onSelect?: (event: ChangeEvent<HTMLInputElement>) => void
}

export const AttachmentInput: React.FC<Props> = forwardRef(
  ({ modalForm, buttonLabel, onSelect, ...props }: Props, ref: ForwardedRef<HTMLInputElement>) => {
    const toast = useAppToast()

    const [selection, setSelection] = useState<
      {
        file: File
        previewUrl: string
      }[]
    >([])
    const { validateFiles } = useValidateFiles()

    const { setValue } = useMemo(
      () => modalForm as UseFormReturn<FieldValues, unknown, FieldValues>,
      [modalForm]
    )

    const getFileDescription = (file: File) => {
      const ext = getFileExtension(file.name)
      return `${file.name.length > 18 ? `${file.name.substring(0, 16)}...` : file.name} (${formatFileSize(file.size)}${isNotBlank(ext) ? `, ${ext.toLocaleUpperCase()}` : ""})`
    }

    const onFileSelect = (event: ChangeEvent<HTMLInputElement>) => {
      const {
        target: { files: newFilesList },
      } = event
      const newFiles = Array.from(newFilesList ?? [])

      const errorMessages = validateFiles(newFiles)
      if (errorMessages !== undefined) {
        toast({ status: "error", title: errorMessages.join("\n") })
      } else {
        setSelection(
          newFiles.map(newFile => ({ file: newFile, previewUrl: URL.createObjectURL(newFile) }))
        )
      }

      onSelect?.(event)
    }

    // The native file input covers the whole field, fully transparent: it stays clickable and
    // focusable, while the browser's own button and "no file" text never show.
    return (
      <Flex
        position="relative"
        alignItems="center"
        gap="3"
        minH="controlH"
        p="1"
        bg="surface"
        border="1px solid"
        borderColor="line"
        _focusWithin={{ outline: "2px solid", outlineColor: "text", outlineOffset: "2px" }}
      >
        <Flex
          alignItems="center"
          gap="2"
          flexShrink={0}
          h="controlHSm"
          px="4"
          bg="surfaceGreige"
          color="text"
          whiteSpace="nowrap"
          data-testid="attachment-button"
        >
          <FileExportIcon boxSize={4} />
          <P3 fontWeight="semibold">
            <FormattedMessage id={buttonLabel ?? "attachment.new_file"} />
          </P3>
        </Flex>
        <P3 color={selection.length < 1 ? "textSubtle" : "text"} noOfLines={1}>
          {selection.length < 1 ? (
            <FormattedMessage id="attachment.no_file" />
          ) : (
            selection.map(s => getFileDescription(s.file)).join(", ")
          )}
        </P3>
        <Input
          {...props}
          type="file"
          onChange={evt => {
            const newFiles = evt.target.files
            const field = props?.name ?? ""
            if (isNotBlank(field))
              setValue(field, props.multiple ? (newFiles as FileList) : (newFiles?.[0] as File))
            onFileSelect(evt)
          }}
          position="absolute"
          inset="0"
          w="full"
          h="full"
          opacity={0}
          cursor="pointer"
          zIndex={1}
        />
      </Flex>
    )
  }
)
